
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  ExternalLink, 
  Check, 
  X, 
  AlertTriangle, 
  Download, 
  ArrowRight,
  Link2, 
  BarChart3
} from "lucide-react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell 
} from 'recharts';
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import { useIsMobile } from "@/hooks/use-mobile";

const backlinksEffectivenessData = [
  { name: "Guest Posts", value: 65, color: "#4338ca" },
  { name: "Resource Links", value: 78, color: "#3b82f6" },
  { name: "Broken Link Building", value: 72, color: "#0ea5e9" },
  { name: "Skyscraper Technique", value: 85, color: "#06b6d4" },
  { name: "HARO Responses", value: 70, color: "#0891b2" },
];

const linkRiskData = [
  { name: "Paid Links", value: 92, color: "#ef4444" },
  { name: "Blog Networks", value: 85, color: "#f97316" },
  { name: "Forum Spam", value: 75, color: "#f59e0b" },
  { name: "Comment Links", value: 70, color: "#eab308" },
  { name: "Directory Submissions", value: 60, color: "#84cc16" },
];

const BacklinkBuilding = () => {
  const isMobile = useIsMobile();
  
  return (
    <PageTransition>
      <Helmet>
        <title>Backlink Building 101 | Los Angeles SEO Group</title>
        <meta
          name="description"
          content="Learn the fundamentals of effective backlink building strategies to improve your website's authority and search rankings."
        />
      </Helmet>

      <Navbar />

      <section className="py-12 md:py-20 bg-gradient-to-b from-muted/50 to-background">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Link2 className="h-7 w-7 text-primary" />
              <h1 className="text-4xl md:text-5xl font-bold">
                Backlink Building <span className="text-gradient bg-sky-gradient">101</span>
              </h1>
            </div>
            
            <div className="flex flex-col md:flex-row gap-8 mb-10">
              <div className="md:w-2/3">
                <p className="text-xl leading-relaxed text-foreground/80 mb-6">
                  Backlinks are one of the most important ranking factors for search engines. 
                  This guide will teach you how to build high-quality backlinks that improve your 
                  website's authority and search rankings.
                </p>
                
                <div className="flex flex-wrap gap-3 mb-6">
                  <span className="bg-muted px-3 py-1 rounded-full text-sm font-medium">Link Building</span>
                  <span className="bg-muted px-3 py-1 rounded-full text-sm font-medium">SEO Strategy</span>
                  <span className="bg-muted px-3 py-1 rounded-full text-sm font-medium">Domain Authority</span>
                </div>
              </div>
              
              <div className="md:w-1/3">
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?auto=format&fit=crop&w=800&q=80" 
                    alt="Backlink visualization on laptop" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            <Tabs defaultValue="fundamentals" className="mb-16">
              <TabsList className="grid grid-cols-2 md:grid-cols-4 mb-8 h-auto p-1">
                <TabsTrigger value="fundamentals" className="py-3">Fundamentals</TabsTrigger>
                <TabsTrigger value="strategies" className="py-3">Strategies</TabsTrigger>
                <TabsTrigger value="tools" className="py-3">Tools</TabsTrigger>
                <TabsTrigger value="mistakes" className="py-3">Common Mistakes</TabsTrigger>
              </TabsList>
              
              <TabsContent value="fundamentals">
                <div className="grid gap-8">
                  <div className="prose prose-lg max-w-none">
                    <div className="flex flex-col md:flex-row gap-8 items-center mb-8">
                      <div className="md:w-1/3">
                        <div className="relative aspect-square rounded-xl overflow-hidden">
                          <img 
                            src="https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=800&q=80" 
                            alt="Code representing backlinks" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                      
                      <div className="md:w-2/3">
                        <h2 className="text-2xl font-bold mb-4">What Are Backlinks?</h2>
                        <p className="text-foreground/80 leading-relaxed mb-4">
                          Backlinks are links from other websites that point to your website. They are like 
                          votes of confidence from one site to another, signaling to search engines that 
                          others vouch for your content.
                        </p>
                        
                        <h3 className="text-xl font-bold mt-6 mb-3">Why Backlinks Matter</h3>
                        <p className="text-foreground/80 leading-relaxed mb-3">
                          Backlinks remain one of Google's top ranking factors. They help search engines:
                        </p>
                        <ul className="space-y-2 mb-6">
                          <li className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>Discover new web pages</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>Determine how well a page should rank in results</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>Understand the content of websites</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold mt-8 mb-4">Quality vs. Quantity</h3>
                    <p className="text-foreground/80 leading-relaxed mb-4">
                      Not all backlinks are created equal. A few high-quality backlinks from authoritative 
                      sites in your industry are worth more than hundreds of low-quality links from 
                      irrelevant or spammy sites.
                    </p>
                    
                    <div className="bg-muted/70 p-6 rounded-xl my-8">
                      <h4 className="font-bold text-lg mb-4">What Makes a Good Backlink?</h4>
                      <ul className="space-y-4">
                        <li className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-foreground/90">Comes from a relevant, authoritative website in your industry</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-foreground/90">Uses contextually relevant anchor text</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-foreground/90">Placed within the main content area of a page</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-foreground/90">Comes from a website with strong SEO metrics</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="mt-8 mb-4">
                      <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        <span>Backlink Quality Impact</span>
                      </h4>
                      <div className="h-[300px] w-full mt-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={backlinksEffectivenessData}
                            margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                            <XAxis 
                              dataKey="name" 
                              angle={-45} 
                              textAnchor="end" 
                              height={70} 
                              tick={{ fontSize: 12 }}
                            />
                            <YAxis 
                              label={{ value: 'Effectiveness Score', angle: -90, position: 'insideLeft' }} 
                              domain={[0, 100]}
                            />
                            <Tooltip />
                            <Bar dataKey="value" name="Effectiveness Score">
                              {backlinksEffectivenessData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="strategies">
                <div className="grid gap-8">
                  <div className="prose prose-lg max-w-none">
                    <h2 className="text-2xl font-bold mb-6">Effective Backlink Building Strategies</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                      <Card className="border-0 shadow-md">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-xl">Content-Based Link Building</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                              <ArrowRight className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                              <span className="text-foreground/80">Create industry studies with original data</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <ArrowRight className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                              <span className="text-foreground/80">Publish comprehensive guides and resources</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <ArrowRight className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                              <span className="text-foreground/80">Use infographics and visual assets</span>
                            </li>
                          </ul>
                        </CardContent>
                      </Card>
                      
                      <Card className="border-0 shadow-md">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-xl">Outreach-Based Link Building</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                              <ArrowRight className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                              <span className="text-foreground/80">Guest blogging on industry websites</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <ArrowRight className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                              <span className="text-foreground/80">Broken link building strategy</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <ArrowRight className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                              <span className="text-foreground/80">Resource page link building</span>
                            </li>
                          </ul>
                        </CardContent>
                      </Card>
                    </div>
                    
                    <div className="flex flex-col md:flex-row gap-8 items-center my-10">
                      <div className="md:w-1/2">
                        <div className="relative aspect-video rounded-xl overflow-hidden">
                          <img 
                            src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=800&q=80" 
                            alt="Skyscraper content strategy" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                      
                      <div className="md:w-1/2">
                        <h3 className="text-xl font-bold mb-4">The Skyscraper Technique</h3>
                        <p className="text-foreground/80 leading-relaxed mb-4">
                          The Skyscraper Technique, popularized by Brian Dean, involves:
                        </p>
                        <ol className="space-y-3 mb-4 list-decimal pl-5">
                          <li className="pl-2 text-foreground/80">Finding popular content in your industry that has many backlinks</li>
                          <li className="pl-2 text-foreground/80">Creating something better (more comprehensive, updated, better designed)</li>
                          <li className="pl-2 text-foreground/80">Reaching out to sites linking to the original content to suggest your improved version</li>
                        </ol>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold mt-8 mb-4">Local Link Building (Los Angeles Focus)</h3>
                    <p className="text-foreground/80 leading-relaxed mb-4">
                      For businesses in Los Angeles, consider these local link building tactics:
                    </p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <li className="bg-muted/50 p-4 rounded-lg flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Join the Los Angeles Area Chamber of Commerce</span>
                      </li>
                      <li className="bg-muted/50 p-4 rounded-lg flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Sponsor local events, sports teams, or nonprofits</span>
                      </li>
                      <li className="bg-muted/50 p-4 rounded-lg flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Get featured in local news publications and blogs</span>
                      </li>
                      <li className="bg-muted/50 p-4 rounded-lg flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Create neighborhood guides or Los Angeles-specific resources</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="tools">
                <div className="grid gap-8">
                  <div className="prose prose-lg max-w-none">
                    <h2 className="text-2xl font-bold mb-6">Essential Backlink Research Tools</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                      <Card className="border-0 shadow-md">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-xl">Backlink Analysis Tools</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-5">
                            <li>
                              <div className="font-semibold text-lg">Ahrefs</div>
                              <p className="text-foreground/80 mt-1">
                                Comprehensive backlink analysis with detailed metrics and competitive insights.
                              </p>
                            </li>
                            <li>
                              <div className="font-semibold text-lg">SEMrush</div>
                              <p className="text-foreground/80 mt-1">
                                Backlink analytics and competitive intelligence with domain comparison features.
                              </p>
                            </li>
                            <li>
                              <div className="font-semibold text-lg">Moz Link Explorer</div>
                              <p className="text-foreground/80 mt-1">
                                Domain authority metrics and link opportunity identification algorithms.
                              </p>
                            </li>
                          </ul>
                        </CardContent>
                      </Card>
                      
                      <Card className="border-0 shadow-md">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-xl">Outreach & Prospecting Tools</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-5">
                            <li>
                              <div className="font-semibold text-lg">Hunter.io</div>
                              <p className="text-foreground/80 mt-1">
                                Find email addresses for outreach campaigns with verification capabilities.
                              </p>
                            </li>
                            <li>
                              <div className="font-semibold text-lg">BuzzStream</div>
                              <p className="text-foreground/80 mt-1">
                                Relationship-based link building platform with outreach templates and tracking.
                              </p>
                            </li>
                            <li>
                              <div className="font-semibold text-lg">Pitchbox</div>
                              <p className="text-foreground/80 mt-1">
                                Automated outreach and relationship management with personalization options.
                              </p>
                            </li>
                          </ul>
                        </CardContent>
                      </Card>
                    </div>
                    
                    <div className="bg-muted/70 p-6 rounded-xl my-8">
                      <h3 className="text-xl font-bold mb-4">How to Track Your Backlink Profile</h3>
                      <p className="text-foreground/80 leading-relaxed mb-4">
                        Regularly monitoring your backlink profile is essential. Set up a schedule to:
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-background p-4 rounded-lg flex items-start gap-3">
                          <div className="bg-primary/10 p-2 rounded-full text-primary">1</div>
                          <div>
                            <span className="font-medium">Track new and lost backlinks</span>
                            <p className="text-sm text-foreground/70 mt-1">Set up alerts for link changes</p>
                          </div>
                        </div>
                        <div className="bg-background p-4 rounded-lg flex items-start gap-3">
                          <div className="bg-primary/10 p-2 rounded-full text-primary">2</div>
                          <div>
                            <span className="font-medium">Analyze the quality of incoming links</span>
                            <p className="text-sm text-foreground/70 mt-1">Review metrics and relevance</p>
                          </div>
                        </div>
                        <div className="bg-background p-4 rounded-lg flex items-start gap-3">
                          <div className="bg-primary/10 p-2 rounded-full text-primary">3</div>
                          <div>
                            <span className="font-medium">Monitor competitor backlink acquisition</span>
                            <p className="text-sm text-foreground/70 mt-1">Identify new opportunities</p>
                          </div>
                        </div>
                        <div className="bg-background p-4 rounded-lg flex items-start gap-3">
                          <div className="bg-primary/10 p-2 rounded-full text-primary">4</div>
                          <div>
                            <span className="font-medium">Identify toxic links for disavowal</span>
                            <p className="text-sm text-foreground/70 mt-1">Protect your site from penalties</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-8 mb-4">
                      <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        <span>Link Risk by Source Type</span>
                      </h4>
                      <div className="h-[300px] w-full mt-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={linkRiskData}
                            margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                            <XAxis 
                              dataKey="name" 
                              angle={-45} 
                              textAnchor="end" 
                              height={70} 
                              tick={{ fontSize: 12 }}
                            />
                            <YAxis 
                              label={{ value: 'Risk Score', angle: -90, position: 'insideLeft' }} 
                              domain={[0, 100]}
                            />
                            <Tooltip />
                            <Bar dataKey="value" name="Risk Score">
                              {linkRiskData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="mistakes">
                <div className="grid gap-8">
                  <div className="prose prose-lg max-w-none">
                    <h2 className="text-2xl font-bold mb-6">Common Backlink Building Mistakes to Avoid</h2>
                    
                    <div className="bg-red-50 border border-red-200 rounded-xl p-6 my-8">
                      <h3 className="text-red-800 flex items-center gap-2 mb-5 text-xl font-bold">
                        <AlertTriangle className="h-5 w-5" />
                        <span>High-Risk Practices to Avoid</span>
                      </h3>
                      
                      <ul className="space-y-5">
                        <li className="flex items-start gap-3">
                          <X className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                          <div>
                            <span className="font-medium text-red-800">Buying links in bulk</span>
                            <p className="text-red-700 mt-1">
                              Purchasing links violates Google's guidelines and can result in penalties.
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <X className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                          <div>
                            <span className="font-medium text-red-800">Participating in link exchanges/schemes</span>
                            <p className="text-red-700 mt-1">
                              Excessive reciprocal linking or link schemes can trigger penalties.
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <X className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                          <div>
                            <span className="font-medium text-red-800">Using automated link building software</span>
                            <p className="text-red-700 mt-1">
                              These tools often create spammy, low-quality links that harm your site.
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <X className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                          <div>
                            <span className="font-medium text-red-800">Over-optimized anchor text</span>
                            <p className="text-red-700 mt-1">
                              Using the same keyword-rich anchor text repeatedly appears unnatural.
                            </p>
                          </div>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="flex flex-col md:flex-row gap-8 items-center my-10">
                      <div className="md:w-1/2">
                        <h3 className="text-xl font-bold mb-4">How to Recover from Bad Backlinks</h3>
                        <p className="text-foreground/80 leading-relaxed mb-4">
                          If you discover toxic backlinks pointing to your site:
                        </p>
                        <ol className="space-y-3 mb-4 list-decimal pl-5">
                          <li className="pl-2 text-foreground/80">Conduct a comprehensive backlink audit</li>
                          <li className="pl-2 text-foreground/80">Contact webmasters to request link removal</li>
                          <li className="pl-2 text-foreground/80">Create a disavow file for links that can't be removed</li>
                          <li className="pl-2 text-foreground/80">Submit the disavow file through Google Search Console</li>
                          <li className="pl-2 text-foreground/80">Monitor your backlink profile regularly going forward</li>
                        </ol>
                      </div>
                      
                      <div className="md:w-1/2">
                        <div className="relative aspect-video rounded-xl overflow-hidden">
                          <img 
                            src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80" 
                            alt="Circuit board representing link networks" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            
            <div className="flex flex-col md:flex-row gap-6 items-start mt-16">
              <Card className="w-full md:w-2/3 border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="text-2xl">Need Professional Help with Backlink Building?</CardTitle>
                  <CardDescription className="text-lg">
                    Our team of Los Angeles SEO experts can develop and implement a 
                    comprehensive backlink strategy tailored to your business.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button asChild size="lg" className="bg-desert-gradient hover:opacity-90">
                      <Link to="/contact">Schedule a Consultation</Link>
                    </Button>
                    <Button variant="outline" size="lg" asChild>
                      <Link to="/case-studies">
                        View Our Case Studies
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="w-full md:w-1/3 bg-muted/50 border-0 shadow-md">
                <CardHeader>
                  <CardTitle>Download Our Resources</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <Button variant="outline" className="w-full justify-start" asChild>
                      <Link to="/resources/on-page-seo-essentials.pdf">
                        <Download className="mr-2 h-4 w-4" />
                        On-Page SEO Essentials PDF
                      </Link>
                    </Button>
                    <Button variant="outline" className="w-full justify-start" asChild>
                      <Link to="/resources/seo-audit-template.xlsx">
                        <Download className="mr-2 h-4 w-4" />
                        SEO Audit Template
                      </Link>
                    </Button>
                    <Button variant="outline" className="w-full justify-start" asChild>
                      <Link to="/knowledge-base/link-schemes-to-avoid">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Link Schemes to Avoid
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </PageTransition>
  );
};

export default BacklinkBuilding;
